/**
 * Utility functions for the company size queue strategy
 */

import { fetchQueries } from '../../api';
import centralDiscoveryQueue from '../../centralDiscoveryQueue';
import { DISCOVERY_TEMPLATE_TYPES } from '../../discoveryQueueSchema';

// Clean confirmation message for session restoration
export const restore_message = "I reviewed your existing workforce, and this is the distribution across different company sizes. Which company size(s) would you like to target?";

/**
 * Generate company size data and populate centralized queue
 * @param {Array} messages - Current conversation messages
 * @param {Function} setMessages - Function to update messages state
 * @param {Object} decision - Current decision object
 * @param {boolean} useCache - Whether to use cached data
 * @returns {Promise<Object>} The company size data
 */
export const generateCompanySizeData = async (messages, setMessages, decision, useCache = true) => {
  // Detect if this is a fresh start (start from scratch or start with JD)
  const isFreshStart = !decision.getAllRequirements || 
    (decision.getAllRequirements && (!decision.getAllRequirements() || decision.getAllRequirements().length === 0));
  
  console.log('📏 COMPANY SIZE: Fresh start detected:', isFreshStart);
  
  // Update thinking accordion
  if (setMessages) {
    setMessages(prev => {
      const updatedMessages = prev.map(msg =>
        msg.role === 'AI' && msg.content === 'Typing...'
          ? {
              ...msg,
              thinking: `${msg.thinking}\nGenerating company size data and populating centralized queue...`
            }
          : msg
      );
      return updatedMessages;
    });
  }

  try {
    console.log('📏 COMPANY SIZE: Starting data generation and queue population');

    // Fetch queries dictionary
    const queries = await fetchQueries();
    if (!queries || Object.keys(queries).length === 0) {
      throw new Error('Failed to fetch queries dictionary');
    }

    console.log('📏 COMPANY SIZE: Fetched queries:', Object.keys(queries).length);

    // Group queries by company size time periods
    const recentCompanySizeQueries = [];
    const currentCompanySizeQueries = [];
    const pastCompanySizeQueries = [];
    const processedQueryIds = new Set(); // Track processed queries to avoid duplicates

    Object.keys(queries).forEach((queryId) => {
      let queryData = queries[queryId];
      queryData.macro_taxonomy = (queryData.macro_taxonomy ? queryData.macro_taxonomy : []);
      
      if (queryData.macro_taxonomy.length === 0 || processedQueryIds.has(queryId)) {
        return;
      }
      
      // Check if any macro_taxonomy contains "Size;Recent"
      const hasRecentCompanySize = queryData.macro_taxonomy.some(tax => 
        tax && typeof tax === 'string' && tax.includes('Size') && tax.includes('Recent')
      );
      
      // Check if any macro_taxonomy contains "Size;Current"
      const hasCurrentCompanySize = queryData.macro_taxonomy.some(tax => 
        tax && typeof tax === 'string' && tax.includes('Size') && tax.includes('Current')
      );
      
      // Check if any macro_taxonomy contains "Size;Past"
      const hasPastCompanySize = queryData.macro_taxonomy.some(tax => 
        tax && typeof tax === 'string' && tax.includes('Size') && tax.includes('Past')
      );
      
      if (hasRecentCompanySize) {
        recentCompanySizeQueries.push({ id: queryId, name: queries[queryId].name });
        processedQueryIds.add(queryId);
        console.log(`📏 COMPANY SIZE: Added to RECENT: ${queries[queryId].name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      } else if (hasCurrentCompanySize) {
        currentCompanySizeQueries.push({ id: queryId, name: queries[queryId].name });
        processedQueryIds.add(queryId);
        console.log(`📏 COMPANY SIZE: Added to CURRENT: ${queries[queryId].name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      } else if (hasPastCompanySize) {
        pastCompanySizeQueries.push({ id: queryId, name: queries[queryId].name });
        processedQueryIds.add(queryId);
        console.log(`📏 COMPANY SIZE: Added to PAST: ${queries[queryId].name} (${queryId}) - macro_taxonomy: ${queryData.macro_taxonomy[0]}`);
      }
    });

    console.log('📏 COMPANY SIZE: Grouped queries:', {
      recent: recentCompanySizeQueries.length,
      current: currentCompanySizeQueries.length,
      past: pastCompanySizeQueries.length
    });

    // Create queue items for each time period
    const queueItems = [];

    // 1. Recent Company Size queue item
    if (recentCompanySizeQueries.length > 0) {
      // For fresh starts, start with empty bool_str - populate only after user selection
      // For existing data analysis, pre-populate with all query IDs
      const recentBoolStr = isFreshStart ? '' : `((${recentCompanySizeQueries.map(q => q.id).join(' OR ')}))`;
      
      const recentQueueItem = {
        queue_id: `company_size_recent_${Date.now()}`,
        section_id: 'company_size_recent',
        section_name: 'Recent Company Size',
        section_type: 'Must Have',
        section_order: 1,
        topic: 'company_size',
        bool_str: recentBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: isFreshStart 
          ? 'What recent company sizes would you like to target for this role?'
          : 'I reviewed your existing workforce, and this is the distribution across different recent company sizes. Which company size(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Recent Company Sizes',
          chart_subtitle: isFreshStart 
            ? 'Select the recent company sizes you want to target'
            : 'Distribution of recent company sizes in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: isFreshStart ? false : true, // For fresh starts, show all options
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: recentCompanySizeQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(recentQueueItem);
    }

    // 2. Current Company Size queue item
    if (currentCompanySizeQueries.length > 0) {
      // For fresh starts, start with empty bool_str - populate only after user selection
      const currentBoolStr = isFreshStart ? '' : `((${currentCompanySizeQueries.map(q => q.id).join(' OR ')}))`;
      
      const currentQueueItem = {
        queue_id: `company_size_current_${Date.now() + 1}`,
        section_id: 'company_size_current',
        section_name: 'Current Company Size',
        section_type: 'Must Have',
        section_order: 2,
        topic: 'company_size',
        bool_str: currentBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: isFreshStart 
          ? 'What current company sizes would you like to target for this role?'
          : 'I reviewed your existing workforce, and this is the distribution across different current company sizes. Which company size(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Current Company Sizes',
          chart_subtitle: isFreshStart 
            ? 'Select the current company sizes you want to target'
            : 'Distribution of current company sizes in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: isFreshStart ? false : true, // For fresh starts, show all options
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: currentCompanySizeQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(currentQueueItem);
    }

    // 3. Past Company Size queue item
    if (pastCompanySizeQueries.length > 0) {
      // For fresh starts, start with empty bool_str - populate only after user selection
      const pastBoolStr = isFreshStart ? '' : `((${pastCompanySizeQueries.map(q => q.id).join(' OR ')}))`;
      
      const pastQueueItem = {
        queue_id: `company_size_past_${Date.now() + 2}`,
        section_id: 'company_size_past',
        section_name: 'Past Company Size',
        section_type: 'Nice to Have',
        section_order: 3,
        topic: 'company_size',
        bool_str: pastBoolStr,
        disco_done: false,
        template_type: DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART,
        ai_intro_message: isFreshStart 
          ? 'What past company sizes would you like to target for this role?'
          : 'I reviewed your existing workforce, and this is the distribution across different past company sizes. Which company size(s) would you like to target?',
        ux_config: {
          title: '',
          chart_title: 'Past Company Sizes',
          chart_subtitle: isFreshStart 
            ? 'Select the past company sizes you want to target'
            : 'Distribution of past company sizes in your workforce',
          confirm_button_text: 'Select at Least One Option',
          allow_empty_selection: true,
          hide_zero_percentage: isFreshStart ? false : true, // For fresh starts, show all options
          allowEmptySelection: true // Allow skipping this discovery by not selecting anything
        },
        template_props: {
          data: pastCompanySizeQueries.map(q => ({
            name: q.name,
            percentage: 0, // Will be calculated dynamically
            queryId: q.id
          })),
          selectedItems: []
        }
      };
      queueItems.push(pastQueueItem);
    }

    // Add all queue items to centralized queue
    console.log('📏 COMPANY SIZE: Adding', queueItems.length, 'items to centralized queue');
    
    // Check if central discovery queue is properly initialized
    if (!centralDiscoveryQueue.sessionId) {
      console.error('❌ COMPANY SIZE: Central discovery queue not initialized - no session ID');
      throw new Error('Central discovery queue not initialized - no session ID');
    }
    
    console.log('✅ COMPANY SIZE: Central discovery queue is initialized with session ID:', centralDiscoveryQueue.sessionId);
    
    for (const queueItem of queueItems) {
      try {
        const addedItem = await centralDiscoveryQueue.addQueueItem(queueItem);
        
        // Calculate items count based on template type
        let itemsCount = 0;
        if (queueItem.template_type === DISCOVERY_TEMPLATE_TYPES.DISTRIBUTION_BAR_CHART) {
          itemsCount = queueItem.template_props.data ? queueItem.template_props.data.length : 0;
        }
        
        console.log('✅ COMPANY SIZE: Added queue item:', {
          section_id: addedItem.section_id,
          section_name: addedItem.section_name,
          template_type: queueItem.template_type,
          items_count: itemsCount
        });
      } catch (error) {
        console.error('❌ COMPANY SIZE: Error adding queue item:', queueItem.section_id, error);
      }
    }

    // Force immediate save to backend database
    try {
      await centralDiscoveryQueue.saveToDatabase();
      console.log('✅ COMPANY SIZE: Successfully synchronized', queueItems.length, 'queue items to backend database');
    } catch (saveError) {
      console.error('❌ COMPANY SIZE: Error saving queue items to backend:', saveError);
    }

    // Update thinking accordion
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nSuccessfully populated ${queueItems.length} company size queue items.`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    console.log('✅ COMPANY SIZE: Successfully populated centralized queue with', queueItems.length, 'items');

    // Return success data for backward compatibility
    return {
      companySizeData: {
        totalItems: queueItems.length,
        recent: recentCompanySizeQueries.length,
        current: currentCompanySizeQueries.length,
        past: pastCompanySizeQueries.length
      },
      totalCount: recentCompanySizeQueries.length + currentCompanySizeQueries.length + pastCompanySizeQueries.length,
      aiMatchesResult: {
        companySizeData: {
          queueItemsCreated: queueItems.length,
          categories: queueItems.map(item => item.section_id)
        },
        companySizeTotal: queueItems.length
      }
    };

  } catch (error) {
    console.error('❌ COMPANY SIZE: Error generating company size data:', error);
    
    // Update thinking accordion with error
    if (setMessages) {
      setMessages(prev => {
        const updatedMessages = prev.map(msg =>
          msg.role === 'AI' && msg.content === 'Typing...'
            ? {
                ...msg,
                thinking: `${msg.thinking}\nError generating company size data: ${error.message}`
              }
            : msg
        );
        return updatedMessages;
      });
    }

    throw error;
  }
};